import { useEffect, useMemo, useState } from "react";
import { Shirt, Sparkles, Droplets, Wind, Thermometer } from "lucide-react";
import { recommend } from "@/lib/clothing/rules";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { cn } from "@/lib/utils";
import { useLocation } from "@/components/location-provider";
import { useUnit } from "@/components/unit-provider";
import { PageHeader } from "@/components/page-header";

interface ClothingItem {
  name: string;
  description: string;
  when_to_wear: string;
  material_info: string;
  warmth_level: number;
  style_tags?: string[];
  category: string;
  score?: number;
}

interface DailyForecast {
  dt: number;
  temp: { day: number; min: number; max: number };
  feels_like?: { day?: number };
  humidity: number;
  wind_speed: number;
  pop?: number;
  weather: { main: string; description: string; icon: string }[];
}

interface CurrentWeather {
  temp: number;
  feels_like: number;
  humidity: number;
  wind_speed: number;
  weather: { main: string; description: string; icon: string }[];
}

const CATEGORY_META: Record<
  string,
  { label: string; icon: string; tone: string; ring: string; chip: string }
> = {
  outerwear: {
    label: "Outerwear",
    icon: "🧥",
    tone: "text-amber-400",
    ring: "ring-amber-500/20",
    chip: "bg-amber-500/10 border-amber-500/20 text-amber-300",
  },
  tops: {
    label: "Tops",
    icon: "👕",
    tone: "text-sky-400",
    ring: "ring-sky-500/20",
    chip: "bg-sky-500/10 border-sky-500/20 text-sky-300",
  },
  bottoms: {
    label: "Bottoms",
    icon: "👖",
    tone: "text-violet-400",
    ring: "ring-violet-500/20",
    chip: "bg-violet-500/10 border-violet-500/20 text-violet-300",
  },
  accessories: {
    label: "Accessories",
    icon: "🧣",
    tone: "text-emerald-400",
    ring: "ring-emerald-500/20",
    chip: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  },
  footwear: {
    label: "Footwear",
    icon: "👟",
    tone: "text-rose-400",
    ring: "ring-rose-500/20",
    chip: "bg-rose-500/10 border-rose-500/20 text-rose-300",
  },
};

function WarmthBar({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-1" aria-label={`Warmth level ${level} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className={cn(
            "h-1.5 w-3 rounded-full transition-colors",
            n <= level ? "bg-primary shadow-sm shadow-primary/30" : "bg-border",
          )}
        />
      ))}
    </div>
  );
}

function strategyFor(tempC: number, main: string, humid: number, windMs: number): string {
  const tempF = (tempC * 9) / 5 + 32;
  const windy = windMs * 2.237 > 15;
  const m = main.toLowerCase();
  const rain = m.includes("rain");
  const snow = m.includes("snow");
  const cold = tempF < 45;
  const cool = tempF >= 45 && tempF < 60;
  const mild = tempF >= 60 && tempF < 75;
  const hot = tempF >= 80;

  const parts: string[] = [];
  if (snow) parts.push("Snowy — go waterproof and insulated");
  else if (rain) parts.push("Wet out — waterproof shell on top");
  else if (cold) parts.push("Cold — layer up with insulation");
  else if (cool) parts.push("Cool — light layers");
  else if (mild) parts.push("Mild — easy single layer");
  else if (hot) parts.push("Hot — light, breathable fabrics");
  if (windy) parts.push("breeze is up, add a windproof layer");
  if (humid > 85 && !rain && !snow) parts.push("damp air, expect a chill");

  return parts.join(" · ");
}

function dayLabel(ts: number, idx: number): string {
  if (idx === 0) return "Today";
  if (idx === 1) return "Tomorrow";
  return new Date(ts * 1000).toLocaleDateString([], { weekday: "short" });
}

function dateLabel(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function ClothingPage() {
  const [current, setCurrent] = useState<CurrentWeather | null>(null);
  const [daily, setDaily] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const { current: location } = useLocation();
  const { display } = useUnit();

  useEffect(() => {
    setLoading(true);
    setActiveIdx(0);
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.weather?.current) setCurrent(d.weather.current);
        if (d.weather?.daily) setDaily(d.weather.daily.slice(0, 7));
      })
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  // Build a day list combining today (using current for today's now-cast) + future days.
  const dayContexts = useMemo(() => {
    if (daily.length === 0) return [];
    return daily.map((d, i) => {
      const isToday = i === 0;
      const tempC = isToday && current ? current.temp : d.temp.day;
      const feelsC =
        isToday && current ? current.feels_like : d.feels_like?.day ?? d.temp.day;
      const humid = isToday && current ? current.humidity : d.humidity;
      const wind = isToday && current ? current.wind_speed : d.wind_speed;
      const mainCondition =
        isToday && current ? current.weather?.[0]?.main : d.weather?.[0]?.main;
      const desc =
        isToday && current
          ? current.weather?.[0]?.description
          : d.weather?.[0]?.description;
      const icon = (isToday && current ? current.weather?.[0]?.icon : d.weather?.[0]?.icon) || "01d";
      return {
        idx: i,
        dt: d.dt,
        label: dayLabel(d.dt, i),
        date: dateLabel(d.dt),
        icon,
        tempC,
        feelsC,
        humid,
        wind,
        hi: d.temp.max,
        lo: d.temp.min,
        pop: Math.round((d.pop || 0) * 100),
        mainCondition: mainCondition || "",
        description: desc || "",
      };
    });
  }, [current, daily]);

  const active = dayContexts[activeIdx];

  const results = useMemo(() => {
    if (!active) return [] as { category: string; items: ClothingItem[] }[];
    return recommend({
      tempC: active.tempC,
      feelsLikeC: active.feelsC,
      humidity: active.humid,
      windSpeedMs: active.wind,
      conditions: active.description,
      mainCondition: active.mainCondition,
    }) as { category: string; items: ClothingItem[] }[];
  }, [active]);

  if (loading) {
    return (
      <div className="space-y-8 animate-slide-up">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="space-y-8 animate-slide-up">
        <PageHeader
          eyebrow="Wardrobe Intelligence"
          title="What to Wear"
          subtitle={`Curated for ${location.name}`}
        />
        <GlassPanel intensity="low" className="p-16 text-center">
          <Shirt className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No forecast data available.</p>
        </GlassPanel>
      </div>
    );
  }

  const strategy = strategyFor(active.feelsC, active.mainCondition, active.humid, active.wind);

  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader
        eyebrow="Wardrobe Intelligence"
        title="What to Wear"
        subtitle={`Curated for ${location.name}`}
      />

      {/* Day strip */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {dayContexts.map((d) => {
          const isActive = d.idx === activeIdx;
          return (
            <button
              key={d.dt}
              onClick={() => setActiveIdx(d.idx)}
              className={cn(
                "rounded-xl border p-2 text-left transition-all",
                isActive
                  ? "border-primary/60 bg-primary/10 shadow-sm shadow-primary/20"
                  : "border-border/40 bg-background/30 hover:border-border/70",
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <p
                  className={cn(
                    "text-[11px] font-bold uppercase tracking-wider",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {d.label}
                </p>
                {d.pop > 30 && (
                  <span className="text-[9px] inline-flex items-center gap-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 px-1.5 py-0">
                    <Droplets className="h-2 w-2" /> {d.pop}%
                  </span>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">{d.date}</p>
              <div className="mt-1 flex items-center gap-1">
                <img
                  src={`/api/icon/${d.icon}.png`}
                  alt=""
                  className="h-7 w-7 -m-1"
                />
                <p className="text-sm font-bold tabular-nums">
                  {Math.round(d.hi)}°
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">
                    /{Math.round(d.lo)}°
                  </span>
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active day context card */}
      <GlassPanel intensity="low" className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl" />
              <img
                src={`/api/icon/${active.icon}@2x.png`}
                alt=""
                className="relative h-20 w-20"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-primary/80 font-semibold">
                {active.label} · {active.date}
              </p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-3xl font-bold tracking-tight">
                  {display(active.tempC)}
                </span>
                <span className="text-xs text-muted-foreground">
                  feels {display(active.feelsC)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">
                {active.description}
              </p>
            </div>
          </div>

          <div className="hidden sm:block w-px self-stretch bg-border/50" />

          <div className="flex-1 min-w-0">
            {strategy && (
              <p className="text-sm font-medium leading-snug">
                <span className="text-primary">Strategy.</span>{" "}
                <span className="capitalize">{strategy}</span>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-xs">
              <div className="flex items-center gap-1.5">
                <Thermometer className="h-3.5 w-3.5 text-orange-400" />
                <span className="text-muted-foreground">High/Low</span>
                <span className="font-semibold">
                  {Math.round(active.hi)}° / {Math.round(active.lo)}°
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Droplets className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-muted-foreground">Humidity</span>
                <span className="font-semibold">{active.humid}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Wind className="h-3.5 w-3.5 text-teal-400" />
                <span className="text-muted-foreground">Wind</span>
                <span className="font-semibold">{active.wind.toFixed(1)} m/s</span>
              </div>
              {active.pop > 0 && (
                <div className="flex items-center gap-1.5">
                  <Droplets className="h-3.5 w-3.5 text-sky-400" />
                  <span className="text-muted-foreground">Precip</span>
                  <span className="font-semibold">{active.pop}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* Outfit list */}
      {results.length === 0 ? (
        <GlassPanel intensity="low" className="p-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/50 mx-auto mb-4">
            <Shirt className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground">
            No specific recommendations for this day.
          </p>
        </GlassPanel>
      ) : (
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Outfit for {active.label}
            </h2>
            <span className="text-[11px] text-muted-foreground">
              {results.reduce((n, g) => n + g.items.length, 0)} picks across {results.length}{" "}
              categories
            </span>
          </div>

          <div className="space-y-3">
            {results.map((group) => {
              const meta =
                CATEGORY_META[group.category] || {
                  label: group.category,
                  icon: "📦",
                  tone: "text-muted-foreground",
                  ring: "ring-muted/20",
                  chip: "bg-muted/10 border-muted/20 text-muted-foreground",
                };
              return (
                <GlassPanel key={group.category} intensity="low" className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex items-center sm:flex-col sm:items-start sm:justify-center gap-3 sm:gap-1.5 sm:w-32 flex-shrink-0">
                      <span
                        className={cn(
                          "inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 text-2xl",
                          meta.ring,
                        )}
                        role="img"
                      >
                        {meta.icon}
                      </span>
                      <div className="sm:mt-1">
                        <h3 className={cn("text-sm font-bold tracking-tight", meta.tone)}>
                          {meta.label}
                        </h3>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {group.items.length} {group.items.length === 1 ? "pick" : "picks"}
                        </p>
                      </div>
                    </div>

                    <div
                      className={cn(
                        "flex-1 grid gap-3",
                        group.items.length > 1 ? "lg:grid-cols-2" : "grid-cols-1",
                      )}
                    >
                      {group.items.map((item) => (
                        <div
                          key={item.name}
                          className="group rounded-xl border border-border/40 bg-background/30 p-4 hover:border-border/70 hover:bg-background/50 transition-colors flex flex-col"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4 className="text-sm font-bold leading-tight">{item.name}</h4>
                            {item.score !== undefined && (
                              <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5">
                                <Sparkles className="h-2.5 w-2.5 text-primary" />
                                <span className="text-[10px] font-bold text-primary tabular-nums">
                                  {Math.min(99, Math.round((item.score / 30) * 100))}%
                                </span>
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                              Warmth
                            </span>
                            <WarmthBar level={item.warmth_level} />
                          </div>

                          <p className="text-xs leading-relaxed text-muted-foreground mt-2.5 line-clamp-2 flex-1">
                            {item.description}
                          </p>

                          {item.style_tags && item.style_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {item.style_tags.slice(0, 4).map((tag) => (
                                <span
                                  key={tag}
                                  className={cn(
                                    "inline-block rounded-full border px-2 py-0.5 text-[10px] capitalize",
                                    meta.chip,
                                  )}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </GlassPanel>
              );
            })}
          </div>

          <p className="mt-6 text-[11px] text-muted-foreground text-center">
            Recommendations based on{" "}
            {active.idx === 0 ? "current conditions" : "forecast"} for {active.label.toLowerCase()}.
          </p>
        </div>
      )}
    </div>
  );
}
